import { ButtonGroup, Flex, Text } from "@chakra-ui/react";
import React from "react";

import { Button } from "../../../components";
import {
  ObjectHeaderBackButton,
  ObjectHeaderContainer,
  ObjectHeaderField,
} from "../../../components/ObjectHeader";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { uppercaseFirstLetterLowercaseRest } from "../../../utils/string";
import { AiInterviewerFragment, AiInterviewerStatus } from "../../graphql";
import {
  AiInterviewerArchiveButton,
  AiInterviewerPublishButton,
  AiInterviewerUnarchiveButton,
} from "./AiInterviewerStatusButton";
import { MutationResult } from "./types";

interface AiInterviewerHeaderProps {
  aiInterviewer: AiInterviewerFragment;
  readOnly?: boolean;
  updateStatusMutation: MutationResult<
    (newStatus: AiInterviewerStatus) => Promise<any>
  >;
  unarchiveMutation?: MutationResult<
    (refetchQueries?: string[]) => Promise<any>
  >;
  onSave: () => void;
  saving?: boolean;
  saveDisabled?: boolean;
}

const AiInterviewerHeader: React.FC<AiInterviewerHeaderProps> = ({
  aiInterviewer,
  readOnly,
  updateStatusMutation,
  unarchiveMutation,
  onSave,
  saving,
  saveDisabled,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const nameValue = aiInterviewer.name?.trim() || "Untitled";

  return (
    <ObjectHeaderContainer>
      <ObjectHeaderBackButton
        backButtonLabel="Back to AI Interviewers"
        backButtonLocation="/ai-interviewers"
      />
      {!isSmallScreen && (
        <Text px="5" mx="4">
          AI Interviewer
        </Text>
      )}
      {isSmallScreen ? (
        <Flex
          direction="column"
          mx={5}
          height={10}
          justifyContent="center"
          maxWidth="180px"
          overflow="hidden"
        >
          <Text fontSize="sm" color="gray.800" noOfLines={1} title={nameValue}>
            {nameValue}
          </Text>
        </Flex>
      ) : (
        <ObjectHeaderField label="AI Interviewer name" value={nameValue} />
      )}
      {!isSmallScreen && (
        <>
          <ObjectHeaderField
            label="Status"
            value={
              aiInterviewer?.status
                ? uppercaseFirstLetterLowercaseRest(
                    String(aiInterviewer.status)
                  )
                : ""
            }
            labelColor={
              aiInterviewer.status === AiInterviewerStatus.Live
                ? "green.500"
                : undefined
            }
          />
          <ObjectHeaderField
            label="Interviews"
            value={aiInterviewer.interviewsCount}
          />
        </>
      )}
      {!isSmallScreen && (
        <ButtonGroup ml="auto">
          <AiInterviewerArchiveButton
            status={aiInterviewer.status}
            updateStatus={updateStatusMutation.mutation}
            updateLoading={updateStatusMutation.loading}
          />
          {unarchiveMutation && (
            <AiInterviewerUnarchiveButton
              status={aiInterviewer.status}
              onUnarchive={() => unarchiveMutation.mutation()}
              loading={unarchiveMutation.loading}
            />
          )}
          {!readOnly && (
            <Button
              size="sm"
              variant="prismSecondary"
              isLoading={saving}
              isDisabled={saveDisabled || updateStatusMutation.loading}
              onClick={onSave}
            >
              Save changes
            </Button>
          )}
          <AiInterviewerPublishButton
            status={aiInterviewer.status}
            updateStatus={updateStatusMutation.mutation}
            updateLoading={updateStatusMutation.loading}
          />
        </ButtonGroup>
      )}
    </ObjectHeaderContainer>
  );
};

export default AiInterviewerHeader;
